import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { getSubmissions } from "../services/submissions";
import Pagination from "../components/utils/Pagination";
import SubmitRequestModal from "../components/utils/SubmitRequestModal";
import RegisterComplaintModal from "../components/utils/RegisterComplaintModal";
import RequestDetailsPanel from "../components/utils/RequestDetailsPanel";
import { FilterDropdown } from "../components/utils/FilterDropdown";
import { FilterGroup } from "../components/utils/FilterGroup";
import { SearchInput } from "../components/utils/SearchInput";
import { SubmissionStatusBadge, SubmissionTypeBadge } from "../components/utils/SubmissionBadges";
import { formatIST, formatDueIn } from "../utils/formatDate";
import { Button } from "../components/utils/Button";
import { Table } from "../components/utils/Table";

const columns = [
  {
    key: "request_id",
    label: "Request ID",
    className: "font-semibold text-gray-800",
    render: (row) => row.request_id || row.id,
  },
  {
    key: "submission_type",
    label: "Type",
    render: (row) => <SubmissionTypeBadge type={row.submission_type} />,
  },
  {
    key: "created_at",
    label: "Submitted On",
    sortable: true,
    className: "text-gray-600",
    render: (row) => formatIST(row.created_at),
  },
  {
    key: "status",
    label: "Status",
    render: (row) => <SubmissionStatusBadge status={row.status} />,
  },
  {
    key: "due_in",
    label: "Response Due In",
    sortable: true,
    className: "text-gray-600",
    render: (row) =>
      (row.status || "").toLowerCase() === "resolved" ? (
        "-"
      ) : (
        <span
          className={
            /overdue/i.test(row.due_in || "")
              ? "text-[#C73838] font-medium"
              : undefined
          }
        >
          {formatDueIn(row.due_in) || "-"}
        </span>
      ),
  },
];

export default function MyRequests() {
  const { companyId } = useParams();
  const location = useLocation();
  const companyName = location.state?.companyName || "Company";

  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortCol, setSortCol] = useState("created_at");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const initialOpenModal = location.state?.openModal || "";
  const [showRequest, setShowRequest] = useState(
    initialOpenModal === "request",
  );
  const [showComplaint, setShowComplaint] = useState(
    initialOpenModal === "complaint",
  );
  const [selectedRequest, setSelectedRequest] = useState(null);
  const pageSize = 10;

  const handleSort = (key) => {
    setSortOrder((o) => (sortCol === key ? (o === "asc" ? "desc" : "asc") : "desc"));
    setSortCol(key);
    setPage(1);
  };

  const fetchRequests = () => {
    setLoading(true);
    getSubmissions(companyId, {
      search,
      submission_type: typeFilter || undefined,
      status: statusFilter || undefined,
      sort_by: "created_at",
      sort_order: sortOrder,
      page,
      page_size: pageSize,
    })
      .then((res) => {
        let items = res.data.data?.items || [];
        // Defensive client-side filter — the API doesn't reliably honor
        // submission_type/status query params, so re-filter what it returns.
        if (typeFilter) {
          items = items.filter(
            (i) =>
              (i.submission_type || "").toLowerCase() ===
              typeFilter.toLowerCase(),
          );
        }
        if (statusFilter) {
          items = items.filter(
            (i) =>
              (i.status || "").toLowerCase() === statusFilter.toLowerCase(),
          );
        }
        setRequests(items);
        setTotal(
          typeFilter || statusFilter ? items.length : res.data.data?.total || 0,
        );
      })
      .catch(() => {
        setRequests([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchRequests, [search, typeFilter, statusFilter, sortOrder, page]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-5">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <FilterGroup
          filters={[typeFilter, statusFilter]}
          onClear={() => {
            setTypeFilter("");
            setStatusFilter("");
            setPage(1);
          }}
        >
          <FilterDropdown
            label="Type"
            value={typeFilter}
            onChange={(value) => {
              setTypeFilter(value);
              setPage(1);
            }}
            options={[
              { value: "request", label: "Update Request" },
              { value: "erasure", label: "Erasure Request" },
              { value: "complaint", label: "Complaint" },
            ]}
          />
          <FilterDropdown
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            options={[
              { value: "resolved", label: "Resolved" },
              { value: "submitted", label: "Submitted" },
              { value: "in review", label: "In Review" },
              { value: "pending", label: "Pending" },
              { value: "closed", label: "Closed" },
            ]}
          />
        </FilterGroup>
        <div className="ml-auto flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowComplaint(true)}
            className="!border-[#C73838] !text-[#C73838] hover:!bg-red-50"
          >
            Register a Complaint
          </Button>
          <Button
            onClick={() => setShowRequest(true)}
            style={{ background: "#2d7a7a" }}
          >
            Submit a Request
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table
          columns={columns}
          rows={requests}
          rowKey={(row, idx) => row.id || idx}
          onRowClick={(row) => setSelectedRequest(row)}
          loading={loading}
          loadingText="Loading..."
          emptyText="No requests found."
          sortCol={sortCol}
          sortDir={sortOrder}
          onSort={handleSort}
        />
      </div>

      <Pagination
        page={page}
        total={total}
        pageSize={pageSize}
        onChange={setPage}
      />

      {showRequest && (
        <SubmitRequestModal
          tenantId={companyId}
          onClose={() => setShowRequest(false)}
          onSuccess={fetchRequests}
        />
      )}
      {showComplaint && (
        <RegisterComplaintModal
          tenantId={companyId}
          companyName={companyName}
          onClose={() => setShowComplaint(false)}
          onSuccess={fetchRequests}
        />
      )}
      {selectedRequest && (
        <RequestDetailsPanel
          request={selectedRequest}
          tenantId={companyId}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  );
}
